import { OnchainIdService, TokenService } from 'services';
import { WebAuthn } from '@dfns/sdk-webauthn';
import { DfnsDelegatedApiClient } from '@dfns/sdk';
import store from '../app/state/store';
import { ethers } from 'ethers';
import { getProvidersForChain } from './EthersProvider';
import BigNumber from 'bignumber.js';

const rpId = window.location.hostname;
const appId = process.env.REACT_APP_DFNS_APP_ID;
const apiUrl = process.env.REACT_APP_DFNS_API;

const register = async ({ walletAddress }) => {
  const challenge = await OnchainIdService.registerInit({ walletAddress });

  if (challenge.challengeSigned) {
    return await OnchainIdService.registerComplete({
      walletAddress,
    });
  } else {
    // https://github.com/dfnsext/typescript-sdk#dfnsdelegatedapiclient
    const webauthn = new WebAuthn({ rpId });
    const attestation = await webauthn.create(challenge);

    return await OnchainIdService.registerComplete({
      walletAddress,
      authToken: challenge.temporaryAuthenticationToken,
      challenge: attestation,
    });
  }
};

// https://github.com/dfnsext/web-ui/blob/m/src/utils/dfns.ts#L205
const transfer = async (token, params) => {
  const { authToken, transactionData, gasEstimation, gasPrice } =
    await TokenService.postValidateTransfer(token.id, params);

  const provider = getProvidersForChain(token.blockchainNetwork);
  const signer = new ethers.VoidSigner(params.senderWallet, provider);

  const transactionToSign = await signer.populateTransaction({
    data: transactionData,
    to: token.address,
    gasLimit: new BigNumber(gasEstimation).multipliedBy(2).toString(),
    gasPrice: gasPrice ?? undefined,
  });
  delete transactionToSign['from'];
  const unsignedTransaction = ethers.utils.serializeTransaction({
    ...transactionToSign,
  });
  const unsignedHash = ethers.utils.keccak256(unsignedTransaction);

  const authn = new WebAuthn({ rpId });
  const apiClient = new DfnsDelegatedApiClient({
    appId,
    baseUrl: apiUrl,
    authToken,
    signer: authn,
  });

  // https://docs.dfns.co/dfns-docs/api-docs/wallets/generate-signature-from-wallet
  const request = {
    walletId: store.getState().holder.dfnsWalletId,
    body: {
      kind: 'Hash',
      hash: unsignedHash.replace(/^0x/, ''),
    },
  };

  const challenge = await apiClient.wallets.generateSignatureInit(request);
  const attestation = await authn.sign(challenge.challenge, challenge.allowCredentials);
  const signature = await apiClient.wallets.generateSignatureComplete(request, {
    challengeIdentifier: challenge.challengeIdentifier,
    firstFactor: attestation,
  });

  const signedTransaction = ethers.utils.serializeTransaction(
    {
      ...transactionToSign,
    },
    signature.signature.encoded,
  );

  return await TokenService.broadcastTransfer(token.id, {
    fromAddress: params.senderWallet,
    data: signedTransaction,
  });
};

export default {
  register,
  transfer,
};
